import React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CATEGORIES, FACING, FURNISHED, AMENITIES, AREA_UNITS, OWNERSHIP, isLandCategory } from "./searchShared";

function Section({ label, children }) {
  return (
    <fieldset className="border-b border-border pb-5">
      <legend className="pb-3 text-[11px] font-black uppercase tracking-[0.18em] text-muted-foreground">{label}</legend>
      {children}
    </fieldset>
  );
}

function CheckRow({ checked, onChange, children, testId }) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-1 py-1.5 text-sm font-semibold text-foreground hover:text-primary" data-testid={testId}>
      <Checkbox checked={checked} onCheckedChange={onChange} /> {children}
    </label>
  );
}

// Redfin-style overlay drawer — the ONLY advanced filter surface on the search page.
export default function PropertyFiltersDrawer({ open, onOpenChange, draft, updateDraft, apply, reset, total, loading, toggleAmenity }) {
  const land = isLandCategory(draft.category);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="filter-sheet">
        <SheetHeader className="border-b border-border px-6 py-4">
          <SheetTitle className="font-display text-xl font-black">All Filters</SheetTitle>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          <Section label="Location">
            <div className="space-y-2">
              <Input placeholder="City" value={draft.city || ""} onChange={(event) => updateDraft({ city: event.target.value })} data-testid="desktop-filter-city" />
              <Input placeholder="Locality / sector" value={draft.locality || ""} onChange={(event) => updateDraft({ locality: event.target.value })} data-testid="desktop-filter-locality" />
            </div>
          </Section>

          <Section label="Listing status">
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "", l: "For Sale" }, { v: "RENT", l: "For Rent" }].map((option) => (
                <button key={option.l} type="button" onClick={() => updateDraft({ listing_type: option.v })} className={`rounded-lg border px-3 py-2 text-sm font-bold ${(draft.listing_type || "") === option.v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`}>{option.l}</button>
              ))}
            </div>
          </Section>

          <Section label="Property type">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => updateDraft({ category: "" })} className={`rounded-lg border px-3 py-2 text-sm font-bold ${!draft.category ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`}>All Types</button>
              {CATEGORIES.map((c) => (
                <button key={c.v} type="button" onClick={() => updateDraft({ category: c.v })} className={`rounded-lg border px-3 py-2 text-sm font-bold ${draft.category === c.v ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`} data-testid={`drawer-category-${c.v.toLowerCase()}`}>{c.l}</button>
              ))}
            </div>
          </Section>

          <Section label="Price (₹)">
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" min="0" placeholder="Min price" value={draft.min_price || ""} onChange={(event) => updateDraft({ min_price: event.target.value })} data-testid="desktop-filter-min-price" />
              <Input type="number" min="0" placeholder="Max price" value={draft.max_price || ""} onChange={(event) => updateDraft({ max_price: event.target.value })} data-testid="desktop-filter-max-price" />
            </div>
          </Section>

          <Section label={land ? "Plot size" : "Area"}>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" min="0" placeholder="Min area" value={draft.min_area || ""} onChange={(event) => updateDraft({ min_area: event.target.value })} />
              <Input type="number" min="0" placeholder="Max area" value={draft.max_area || ""} onChange={(event) => updateDraft({ max_area: event.target.value })} />
            </div>
            <select value={draft.area_unit || ""} onChange={(event) => updateDraft({ area_unit: event.target.value })} className="mt-2 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm font-semibold" aria-label="Area unit" data-testid="desktop-filter-area-unit">
              <option value="">Any area unit</option>
              {AREA_UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
            </select>
          </Section>

          {land && (
            <Section label="Land details">
              <div className="space-y-0.5">
                <CheckRow checked={draft.corner_plot === "true"} onChange={(v) => updateDraft({ corner_plot: v ? "true" : "" })}>Corner plot</CheckRow>
                <CheckRow checked={draft.registry_available === "true"} onChange={(v) => updateDraft({ registry_available: v ? "true" : "" })}>Registry available</CheckRow>
                <CheckRow checked={draft.mutation_available === "true"} onChange={(v) => updateDraft({ mutation_available: v ? "true" : "" })}>Mutation available</CheckRow>
              </div>
            </Section>
          )}

          <Section label="Verification">
            <div className="space-y-0.5">
              <CheckRow checked={draft.verified_documents === "true"} onChange={(v) => updateDraft({ verified_documents: v ? "true" : "" })} testId="drawer-verified-docs">Verified documents only</CheckRow>
              <CheckRow checked={draft.verified_owner === "true"} onChange={(v) => updateDraft({ verified_owner: v ? "true" : "" })}>Verified owner only</CheckRow>
            </div>
          </Section>

          <Section label="Facing">
            <div className="flex flex-wrap gap-1.5">
              {FACING.map((f) => (
                <button key={f} type="button" onClick={() => updateDraft({ facing: draft.facing === f ? "" : f })} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${draft.facing === f ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground/30"}`}>{f}</button>
              ))}
            </div>
          </Section>

          <Section label="Ownership">
            <div className="flex flex-wrap gap-1.5">
              {OWNERSHIP.map((o) => (
                <button key={o} type="button" onClick={() => updateDraft({ ownership_type: draft.ownership_type === o ? "" : o })} className={`rounded-full border px-3 py-1.5 text-xs font-bold capitalize ${draft.ownership_type === o ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground/30"}`}>{o.replaceAll("_", " ")}</button>
              ))}
            </div>
          </Section>

          {!land && (
            <Section label="Built-up details">
              <div className="flex flex-wrap gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => updateDraft({ bedrooms: String(draft.bedrooms) === String(n) ? "" : String(n) })} className={`rounded-full border px-3.5 py-1.5 text-xs font-bold ${String(draft.bedrooms) === String(n) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground/30"}`} data-testid={`desktop-filter-bed-${n}`}>{n}+ BHK</button>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {FURNISHED.map((f) => (
                  <button key={f.v} type="button" onClick={() => updateDraft({ furnished: draft.furnished === f.v ? "" : f.v })} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${draft.furnished === f.v ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground/30"}`}>{f.l}</button>
                ))}
              </div>
            </Section>
          )}

          <Section label="Amenities">
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
              {AMENITIES.map((a) => (
                <CheckRow key={a} checked={(draft.amenities || []).includes(a)} onChange={() => toggleAmenity(a)} testId={`desktop-filter-amen-${a}`}>{a}</CheckRow>
              ))}
            </div>
          </Section>
        </div>

        <div className="grid grid-cols-[auto_1fr] items-center gap-3 border-t border-border bg-background px-6 py-4">
          <button type="button" onClick={reset} className="text-sm font-bold text-muted-foreground underline-offset-4 hover:text-primary hover:underline" data-testid="desktop-filter-reset">Reset all</button>
          <Button type="button" onClick={() => { apply(); onOpenChange(false); }} className="h-11 rounded-full text-sm font-black" data-testid="desktop-filter-apply">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {Number.isFinite(Number(total)) ? `Show ${Number(total).toLocaleString("en-IN")} ${Number(total) === 1 ? "property" : "properties"}` : "Show results"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
